import { useState } from 'react';
import { Alert, App, Button, Card, Descriptions, Result, Space, Table, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  ArrowLeftOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  ReloadOutlined,
  SyncOutlined,
} from '@ant-design/icons';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type { AdminConnector } from '@goelectric/shared-types';
import { ApiError } from '@/lib/http/apiClient';
import { stationApi } from '../api/stationApi';
import { ConnectorFormModal } from '../components/ConnectorFormModal';
import { StationFormModal } from '../components/StationFormModal';
import {
  StationOperationBadge,
  StationRegistryBadge,
} from '../components/StationBadges';
import { useStation } from '../hooks/useStation';
import { describeStationApiError } from '../utils/stationErrors';
import {
  CONNECTOR_STANDARD_LABELS,
  OCPP_VERSION_LABELS,
  POWER_TYPE_LABELS,
  formatDateTime,
  stationDisplayName,
} from '../utils/format';

/** Conectores: inventario del CRM (el estado en vivo lo sirve el NOC). */
const CONNECTOR_COLUMNS: ColumnsType<AdminConnector> = [
  { title: 'Nº', dataIndex: 'id', key: 'id', width: 70 },
  {
    title: 'Estándar',
    dataIndex: 'standard',
    key: 'standard',
    width: 120,
    render: (standard: AdminConnector['standard']) => CONNECTOR_STANDARD_LABELS[standard],
  },
  {
    title: 'Tipo de potencia',
    key: 'powerType',
    width: 160,
    render: (_, connector) =>
      connector.powerType ? POWER_TYPE_LABELS[connector.powerType] : '—',
  },
  {
    title: 'Potencia',
    key: 'maxPowerKw',
    width: 120,
    render: (_, connector) => `${connector.maxPowerKw} kW`,
  },
  {
    title: 'Voltaje',
    key: 'maxVoltageV',
    width: 110,
    render: (_, connector) => (connector.maxVoltageV ? `${connector.maxVoltageV} V` : '—'),
  },
  {
    title: 'Amperaje',
    key: 'maxAmperageA',
    width: 110,
    render: (_, connector) => (connector.maxAmperageA ? `${connector.maxAmperageA} A` : '—'),
  },
];

/**
 * Detalle de Cargador (US-2.2, `/cargadores/:stationId`). Header con volver + identidad
 * (`Ubicación · ChargePoint ID`) + badges de estado/sincronización y acciones de escritura:
 * sincronizar, editar (modal PATCH), desactivar (DELETE con confirmación) y reactivar
 * (PATCH `status: ACTIVE`). Tres tarjetas (identidad, ubicación, estado/sincronización con
 * fechas) más el CRUD de conectores (alta/edición/eliminación desde la tabla).
 *
 * NO se gatea por rol: la sección de Cargadores es solo `SUPER_ADMIN` (guard de ruta),
 * así que quien llega acá siempre puede escribir.
 */
export function StationDetailPage() {
  const { stationId } = useParams<{ stationId: string }>();
  const navigate = useNavigate();
  const { message, modal } = App.useApp();
  const { station, loading, error, notFound, refresh } = useStation(stationId);
  const [syncing, setSyncing] = useState(false);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [statusSubmitting, setStatusSubmitting] = useState(false);
  const [connectorModal, setConnectorModal] = useState<{
    open: boolean;
    connector: AdminConnector | null;
  }>({ open: false, connector: null });

  const goToList = () => navigate('/cargadores');

  /**
   * Ejecuta un cambio de estado (desactivar/reactivar) mostrando el mensaje real del
   * backend y refrescando el detalle. Comparte `statusSubmitting` para bloquear ambos
   * botones mientras corre la petición.
   */
  const changeStatus = async (
    action: () => Promise<unknown>,
    successMessage: string,
    fallbackMessage: string,
  ) => {
    if (!stationId) return;
    setStatusSubmitting(true);
    try {
      await action();
      message.success(successMessage);
      refresh();
    } catch (caught) {
      message.error(describeStationApiError(caught, fallbackMessage).message);
    } finally {
      setStatusSubmitting(false);
    }
  };

  /** Desactivar saca el cargador de la operación pero conserva su historial. */
  const handleDeactivate = () => {
    if (!stationId || !station) return;
    modal.confirm({
      title: 'Desactivar cargador',
      content: `${stationDisplayName(station)} saldrá de la operación. El historial se conserva y puedes reactivarlo cuando quieras.`,
      okText: 'Desactivar',
      okButtonProps: { danger: true },
      cancelText: 'Cancelar',
      onOk: () =>
        changeStatus(
          () => stationApi.remove(stationId),
          'El cargador quedó dado de baja.',
          'No se pudo desactivar el cargador.',
        ),
    });
  };

  const handleReactivate = () => {
    if (!stationId) return;
    void changeStatus(
      () => stationApi.update(stationId, { status: 'ACTIVE' }),
      'El cargador quedó activo.',
      'No se pudo reactivar el cargador.',
    );
  };

  const handleDeleteConnector = (connector: AdminConnector) => {
    if (!stationId) return;
    modal.confirm({
      title: 'Eliminar conector',
      content: `El conector Nº ${connector.id} se quitará del cargador.`,
      okText: 'Eliminar',
      okButtonProps: { danger: true },
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          await stationApi.removeConnector(stationId, connector.id);
          message.success('Conector eliminado.');
          refresh();
        } catch (caught) {
          message.error(describeStationApiError(caught, 'No se pudo eliminar el conector.').message);
        }
      },
    });
  };

  const handleSync = async () => {
    if (!stationId) return;
    setSyncing(true);
    setSyncError(null);
    try {
      await stationApi.sync(stationId);
      message.success('Sincronización reintentada. El cargador se actualizará en breve.');
      refresh();
    } catch (caught) {
      // Igual que en Locaciones: el error del sync vive DENTRO de la tarjeta de estado,
      // junto al badge (no en un Alert arriba de las tarjetas).
      if (caught instanceof ApiError && caught.status === 502) {
        setSyncError(
          'El core no respondió: no se pudo sincronizar la estación en CitrineOS. El cargador sigue en estado de error; puedes reintentar.',
        );
      } else {
        setSyncError(describeStationApiError(caught).message);
      }
    } finally {
      setSyncing(false);
    }
  };

  if (loading) {
    return <Card loading title="Cargador" />;
  }

  if (notFound) {
    return (
      <Result
        status="404"
        title="Cargador no encontrado"
        subTitle="El cargador no existe o fue eliminado."
        extra={<Button onClick={goToList}>Volver al listado</Button>}
      />
    );
  }

  if (error || !station) {
    return (
      <Result
        status="error"
        title="No se pudo cargar el cargador"
        subTitle={error ?? undefined}
        extra={
          <Space>
            <Button type="primary" onClick={refresh}>
              Reintentar
            </Button>
            <Button onClick={goToList}>Volver al listado</Button>
          </Space>
        }
      />
    );
  }

  const canSync = station.syncStatus === 'PENDIENTE' || station.syncStatus === 'ERROR';

  const connectorColumns: ColumnsType<AdminConnector> = [
    ...CONNECTOR_COLUMNS,
    {
      title: 'Acciones',
      key: 'actions',
      width: 110,
      render: (_, connector) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined />}
            aria-label={`Editar conector ${connector.id}`}
            onClick={() => setConnectorModal({ open: true, connector })}
          />
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            aria-label={`Eliminar conector ${connector.id}`}
            onClick={() => handleDeleteConnector(connector)}
          />
        </Space>
      ),
    },
  ];

  return (
    <div className="cargadores-page">
      <div className="cargadores-page__header">
        <Space align="center" wrap>
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            aria-label="Volver al listado de cargadores"
            onClick={goToList}
          />
          <Typography.Title level={3} style={{ margin: 0 }}>
            {stationDisplayName(station)}
          </Typography.Title>
          <StationRegistryBadge status={station.registryStatus} />
          <StationOperationBadge
            status={station.operationStatus ?? null}
            reason={station.operationReason ?? null}
          />
        </Space>
        <Space>
          {canSync && (
            <Button icon={<SyncOutlined />} loading={syncing} onClick={handleSync}>
              Reintentar sincronización
            </Button>
          )}
          <Button icon={<EditOutlined />} onClick={() => setEditOpen(true)}>
            Editar
          </Button>
          {station.status === 'ACTIVE' && (
            <Button
              danger
              icon={<DeleteOutlined />}
              loading={statusSubmitting}
              onClick={handleDeactivate}
            >
              Desactivar
            </Button>
          )}
          {station.status === 'INACTIVE' && (
            <Button
              type="primary"
              icon={<ReloadOutlined />}
              loading={statusSubmitting}
              onClick={handleReactivate}
            >
              Reactivar
            </Button>
          )}
        </Space>
      </div>

      <Card title="Identidad">
        <Descriptions column={2} bordered size="small">
          <Descriptions.Item label="ChargePoint ID">
            <span style={{ fontFamily: 'var(--ge-font-mono)' }}>{station.id}</span>
          </Descriptions.Item>
          <Descriptions.Item label="Marca">{station.vendor || '—'}</Descriptions.Item>
          <Descriptions.Item label="Modelo">{station.model || '—'}</Descriptions.Item>
          <Descriptions.Item label="Protocolo">
            {OCPP_VERSION_LABELS[station.protocol]}
          </Descriptions.Item>
          <Descriptions.Item label="Potencia máx" span={2}>
            {station.maxPowerKw} kW
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Card title="Ubicación">
        <Descriptions column={2} bordered size="small">
          <Descriptions.Item label="Ubicación" span={2}>
            <Link to={`/ubicaciones/${encodeURIComponent(station.locationId)}`}>
              {station.location?.name ?? station.locationId}
            </Link>
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Card title="Registro y operación">
        <Descriptions column={2} bordered size="small">
          <Descriptions.Item label="Registro">
            <StationRegistryBadge status={station.registryStatus} />
          </Descriptions.Item>
          <Descriptions.Item label="Operación">
            <Space direction="vertical" size={4} style={{ maxWidth: 480 }}>
              <StationOperationBadge
                status={station.operationStatus ?? null}
                reason={station.operationReason ?? null}
              />
              {syncError && <Alert type="error" showIcon role="alert" message={syncError} />}
            </Space>
          </Descriptions.Item>
          <Descriptions.Item label="Creado">{formatDateTime(station.createdAt)}</Descriptions.Item>
          <Descriptions.Item label="Última actualización">
            {formatDateTime(station.updatedAt)}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Card
        title="Conectores"
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setConnectorModal({ open: true, connector: null })}
          >
            Agregar conector
          </Button>
        }
      >
        <Table<AdminConnector>
          rowKey="id"
          size="small"
          columns={connectorColumns}
          dataSource={station.connectors}
          pagination={false}
          locale={{ emptyText: 'Este cargador no tiene conectores.' }}
        />
      </Card>

      <StationFormModal
        open={editOpen}
        station={station}
        onClose={() => setEditOpen(false)}
        onSaved={refresh}
      />

      <ConnectorFormModal
        open={connectorModal.open}
        stationId={station.id}
        cabinetKw={station.maxPowerKw}
        connector={connectorModal.connector}
        existingConnectorIds={station.connectors.map((connector) => connector.id)}
        onClose={() => setConnectorModal((state) => ({ ...state, open: false }))}
        onSaved={refresh}
      />
    </div>
  );
}
